/**
 * Runtime host mode for Consark SSO login hosts.
 * APP_HOST = shared app host (discover/picker); DEDICATED = tenant subdomain.
 *
 * Loopback (`127.0.0.1` / `localhost` / `::1`) is one *login mode* group so
 * APP_HOST detection works regardless of spelling. Mongo hostname is the
 * request host (this laptop seeds `127.0.0.1`) — FE does not rewrite it.
 *
 * L0 (no BFF): Login uses `normal` and does not depend on hostMode.
 * L1 (BFF configured): empty APP_HOST + loopback → APP_HOST.
 */

function stripHostPort(hostname) {
  if (!hostname) return '';
  let h = String(hostname).split(',')[0].trim().toLowerCase();
  const bracketed = h.match(/^\[([^\]]+)\](?::\d+)?$/);
  if (bracketed) {
    h = bracketed[1];
  } else if (/^\d{1,3}(?:\.\d{1,3}){3}(?::\d+)?$/.test(h)) {
    h = h.replace(/:\d+$/, '');
  } else if (h === '::1') {
    // bare IPv6 loopback — do not treat trailing `:1` as a port
  } else {
    h = h.replace(/:\d+$/, '');
  }
  return h.replace(/\.$/, '');
}

export function isLoopbackHostname(hostname) {
  const h = stripHostPort(hostname);
  return h === 'localhost' || h === '127.0.0.1' || h === '::1';
}

export function canonicalizeHostname(hostname) {
  return stripHostPort(hostname);
}

function isAuthBffUrlConfigured() {
  return Boolean(
    String(process.env.VUE_APP_AUTH_BFF_BASE_PATH || '').trim() ||
    String(process.env.VUE_APP_AUTH_BFF_URL || '').trim(),
  );
}

export function isSsoLocalDevEnabled() {
  return String(process.env.VUE_APP_SSO_LOCAL_DEV || '').toLowerCase() === 'true';
}

/**
 * L1 safety: never assign `https://{dedicated}/...` from localhost.
 * Also honors `VUE_APP_SSO_LOCAL_DEV` if a hosted BFF still returns `redirect`.
 */
export function shouldCollapseDedicatedNavigation(
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
) {
  const collapse = isSsoLocalDevEnabled() || isLoopbackHostname(hostname);
  return collapse;
}

export function isSharedHostingMode(hostingMode, isAppHost = false) {
  return hostingMode === 'SHARED_APP_HOST' || isAppHost === true;
}

/** Map Auth BFF domain row → FE hostMode (source of truth on hosted SHARED hosts). */
export function hostModeFromDomain(domain = {}) {
  if (isSharedHostingMode(domain.hostingMode, domain.isAppHost)) return 'APP_HOST';
  if (domain.hostingMode === 'DEDICATED_SUBDOMAIN') return 'DEDICATED';
  return 'UNKNOWN';
}

export function resolveHostMode(hostname = typeof window !== 'undefined' ? window.location.hostname : '') {
  const appHost = String(process.env.VUE_APP_APP_HOST || '').trim().toLowerCase();
  const host = String(hostname || '').trim().toLowerCase();
  const bffConfigured = isAuthBffUrlConfigured();
  let mode;

  if (!host) {
    mode = 'UNKNOWN';
  } else if (isLoopbackHostname(host)) {
    if (isLoopbackHostname(appHost)) {
      mode = 'APP_HOST';
    } else {
      // Empty APP_HOST, or leftover testdevapp APP_HOST while browsing loopback:
      // never DEDICATED. APP_HOST only when BFF is configured (L1).
      mode = bffConfigured ? 'APP_HOST' : 'UNKNOWN';
    }
  } else if (!appHost) {
    mode = 'UNKNOWN';
  } else if (canonicalizeHostname(host) === canonicalizeHostname(appHost)) {
    mode = 'APP_HOST';
  } else {
    mode = 'DEDICATED';
  }

  return mode;
}

export function isAppHost(hostname) {
  return resolveHostMode(hostname) === 'APP_HOST';
}

/**
 * Org switcher is hidden only when the host is positively DEDICATED.
 * UNKNOWN (L0 / local) is not dedicated and must keep the existing switcher.
 */
export function shouldShowOrganisationSwitcher(hostMode) {
  return hostMode !== 'DEDICATED';
}

export function getAppHost() {
  return String(process.env.VUE_APP_APP_HOST || '').trim().toLowerCase();
}
